import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, LogOut, Loader2, ArrowLeft, Sparkles, Star, Send, CheckCircle2, Clock } from "lucide-react";
import { LOGO_URL } from "../data/content";
import { CloudCanvas } from "../components/CloudCanvas";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "amyu_loyalty_card_id";

// Cloud-shaped stamp (SVG). Fills with coral when a tampon is earned.
const StampCircle = ({ filled, index }) => (
  <div
    className="relative aspect-square flex items-center justify-center transition-all duration-500"
    style={{ transitionDelay: `${index * 40}ms` }}
    data-testid={`stamp-${index}${filled ? "-filled" : ""}`}
  >
    <svg
      viewBox="0 0 64 42"
      className="absolute inset-0 w-full h-full"
      style={{
        filter: filled
          ? "drop-shadow(0 6px 12px rgba(229,91,91,0.45))"
          : "none",
      }}
    >
      <path
        d="M14 34 C6 34 2 28 6 22 C3 15 9 9 16 12 C17 4 28 2 32 9 C36 2 47 4 48 12 C55 9 61 15 58 22 C62 28 58 34 50 34 Z"
        fill={filled ? "#e55b5b" : "rgba(255,255,255,0.7)"}
        stroke={filled ? "#e55b5b" : "rgba(229,91,91,0.45)"}
        strokeWidth={filled ? 0 : 1.2}
        strokeDasharray={filled ? "0" : "3 3"}
      />
    </svg>
    <span
      className={`relative font-display text-[13px] leading-none ${
        filled ? "text-white" : "text-amyu-muted"
      }`}
      style={{ marginTop: "2px" }}
    >
      {filled ? "★" : index + 1}
    </span>
  </div>
);

const LoyaltyCardVisual = ({ card, qrValue, cardRef }) => {
  const stamps = card.stamps;
  const threshold = card.reward_threshold || 10;
  return (
    <div
      ref={cardRef}
      data-testid="loyalty-card-visual"
      className="relative rounded-[32px] overflow-hidden text-amyu-ink"
      style={{
        background: "linear-gradient(135deg,#ffe6ec 0%,#fff5d6 50%,#ffd8e2 100%)",
        boxShadow: "0 30px 80px rgba(229,91,91,0.18)",
        padding: "32px 28px",
        width: "100%",
        maxWidth: 420,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.6), transparent 50%), radial-gradient(circle at 90% 90%, rgba(255,255,255,0.5), transparent 55%)",
        }}
      />
      <div className="relative flex items-center justify-between">
        <img src={LOGO_URL} alt="Amyu" className="h-10" crossOrigin="anonymous" />
        <span className="text-[10px] tracking-[0.28em] uppercase text-coral font-medium">
          Carte Fidélité
        </span>
      </div>

      <div className="relative mt-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-amyu-muted">Titulaire</div>
        <div className="font-display text-2xl mt-1 leading-tight">{card.name}</div>
        <div className="text-xs text-amyu-muted mt-0.5">{card.email}</div>
      </div>

      <div className="relative mt-6">
        <div className="flex items-baseline justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] text-amyu-muted">
            Progression
          </div>
          <div className="text-xs text-amyu-ink font-medium">
            {stamps}/{threshold}
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2 mt-3">
          {Array.from({ length: threshold }).map((_, i) => (
            <StampCircle key={i} filled={i < stamps} index={i} />
          ))}
        </div>
        <div className="mt-3 text-[11px] text-amyu-muted italic">
          {stamps >= threshold
            ? "🎉 Une gourmandise t'attend — présente ta carte !"
            : `Plus que ${threshold - stamps} visite${
                threshold - stamps > 1 ? "s" : ""
              } pour un tiramisu offert ♡`}
        </div>
      </div>

      <div className="relative mt-6 flex items-end justify-between gap-4">
        <div className="text-[10px] leading-relaxed text-amyu-muted max-w-[55%]">
          <div className="uppercase tracking-[0.2em] text-coral font-medium mb-1">
            Comment ça marche
          </div>
          Présente ce QR à chaque visite. À 10 tampons, on t&apos;offre un tiramisu ♡
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-md shrink-0">
          <QRCodeSVG
            value={qrValue}
            size={96}
            level="M"
            fgColor="#2a2422"
            bgColor="#ffffff"
          />
        </div>
      </div>

      <div
        className="relative mt-5 pt-4 border-t border-dashed border-coral/30 flex justify-between text-[10px] uppercase tracking-[0.2em] text-amyu-muted"
      >
        <span>Amyu · Homemade</span>
        <span>ID · {card.id.slice(0, 8).toUpperCase()}</span>
      </div>
    </div>
  );
};

const SignupForm = ({ onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/loyalty/signup`, {
        name: name.trim(),
        email: email.trim(),
      });
      localStorage.setItem(STORAGE_KEY, r.data.card.id);
      toast.success(
        r.data.already ? "On t'a retrouvé — bonjour !" : "Bienvenue dans le club Amyu !"
      );
      onSuccess(r.data.card);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="loyalty-signup-form"
      className="bg-white/80 backdrop-blur-md rounded-[32px] p-8 max-w-md w-full mx-auto"
      style={{ boxShadow: "0 20px 60px rgba(42,36,34,0.08)" }}
    >
      <div className="flex justify-center mb-5">
        <div className="w-14 h-14 rounded-full bg-amyu-pink flex items-center justify-center">
          <Sparkles className="text-coral" size={22} />
        </div>
      </div>
      <h1 className="font-display text-3xl md:text-4xl text-center leading-tight">
        Crée <span className="italic text-coral">ta carte de fidélité</span>
      </h1>
      <p className="text-center text-sm text-amyu-muted mt-3">
        1 tampon par visite. 10 tampons = 1 tiramisu offert ♡
      </p>

      <div className="mt-6 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-amyu-muted">
            Prénom
          </label>
          <input
            data-testid="loyalty-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Amy"
            className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-amyu-muted">
            Email
          </label>
          <input
            data-testid="loyalty-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="amy@exemple.fr"
            className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="loyalty-signup-submit"
        className="mt-6 w-full bg-coral text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-coral/90 transition-all disabled:opacity-60"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? "Création..." : "Créer ma carte"}
      </button>
      <p className="text-[11px] text-amyu-muted text-center mt-4">
        On retrouve automatiquement ta carte si tu es déjà inscrit·e.
      </p>
    </form>
  );
};

const ReviewMission = ({ card, onSuccess }) => {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const status = card.review_status;

  if (status === "approved") {
    return (
      <div className="mt-6 bg-amyu-pistachio/25 rounded-2xl p-5 flex items-start gap-3">
        <CheckCircle2 className="text-amyu-pistachio-dark shrink-0 mt-0.5" size={20} />
        <div>
          <div className="font-medium text-sm">Mission accomplie ♡</div>
          <div className="text-xs text-amyu-muted mt-0.5">Ton avis est en ligne et un tampon t'a été offert.</div>
        </div>
      </div>
    );
  }
  if (status === "pending") {
    return (
      <div className="mt-6 bg-amyu-cream rounded-2xl p-5 flex items-start gap-3">
        <Clock className="text-amyu-muted shrink-0 mt-0.5" size={20} />
        <div>
          <div className="font-medium text-sm">Avis en attente de validation</div>
          <div className="text-xs text-amyu-muted mt-0.5">On regarde ça et tu recevras ton +1 tampon dès validation ♡</div>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!text.trim()) return toast.error("Écris un petit mot ♡");
    setBusy(true);
    try {
      await axios.post(`${API}/loyalty/cards/${card.id}/review`, { rating, text: text.trim() });
      toast.success("Merci ! On valide ton avis très vite ♡");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white/85 backdrop-blur-md rounded-2xl p-5 border border-amyu/40" data-testid="loyalty-review-mission">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-coral" />
        <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">Mission — +1 tampon</span>
      </div>
      <h3 className="font-display text-xl mt-2">Écris ton premier avis ♡</h3>
      <p className="text-xs text-amyu-muted mt-1">Après validation, on t'offre 1 tampon bonus.</p>
      <div className="flex gap-1 mt-3" data-testid="loyalty-review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setRating(n)} data-testid={`loyalty-review-star-${n}`}>
            <Star size={22} className={n <= rating ? "text-coral fill-coral" : "text-amyu-muted"} />
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Raconte-nous ton expérience Amyu…"
        data-testid="loyalty-review-text"
        className="mt-3 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30 resize-none text-sm"
      />
      <button
        onClick={submit}
        disabled={busy || !text.trim()}
        data-testid="loyalty-review-submit"
        className="mt-3 btn-coral inline-flex items-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer mon avis
      </button>
    </div>
  );
};

export default function LoyaltyCard() {
  usePageMeta({
    title: "Carte de fidélité Amyu | 10 tampons = 1 tiramisu offert 🌸",
    description:
      "Rejoins gratuitement la carte de fidélité Amyu : 1 tampon par visite, 10 tampons = 1 tiramisu offert. Tiramisus artisanaux fait maison à Carcassonne (Aude).",
    canonical: "https://amyu.fr/carte-fidelite",
  });
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    const cardId = localStorage.getItem(STORAGE_KEY);
    if (!cardId) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/loyalty/cards/${cardId}`)
      .then((r) => setCard(r.data.card))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
      })
      .finally(() => setLoading(false));
  }, []); // one-shot on mount: reads localStorage + fetches card

  // Refresh card every 15s (so stamps update if admin scans while user watches).
  // We depend only on card?.id — full card object churn would restart the interval on every refresh.
  const cardId = card?.id;
  useEffect(() => {
    if (!cardId) return;
    const id = setInterval(() => {
      axios
        .get(`${API}/loyalty/cards/${cardId}`)
        .then((r) => setCard(r.data.card))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [cardId]);

  const qrValue = card ? `${window.location.origin}/s/${card.id}` : "";

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#fbf7f2",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `amyu-carte-fidelite-${card.name.toLowerCase().replace(/\s/g, "-")}.png`;
      link.click();
      toast.success("Carte téléchargée — pense à l'ajouter à ton Wallet !");
    } catch (err) {
      toast.error("Impossible de télécharger l'image");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCard(null);
  };

  return (
    <div className="relative min-h-screen bg-amyu-bg overflow-hidden">
      <CloudCanvas />
      <div className="relative z-10 max-w-5xl mx-auto px-5 py-10 md:py-16">
        <a
          href="/"
          data-testid="loyalty-back-link"
          className="inline-flex items-center gap-2 text-sm text-amyu-muted hover:text-coral transition-colors mb-8"
        >
          <ArrowLeft size={14} /> Retour au site
        </a>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin text-coral" size={32} />
          </div>
        ) : !card ? (
          <SignupForm onSuccess={setCard} />
        ) : (
          <div className="flex flex-col md:flex-row gap-10 items-center md:items-start">
            <div className="w-full md:w-auto flex justify-center">
              <LoyaltyCardVisual card={card} qrValue={qrValue} cardRef={cardRef} />
            </div>
            <div className="flex-1 max-w-md">
              <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">
                Ta carte fidélité
              </span>
              <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                Bonjour <span className="italic text-coral">{card.name}</span>
              </h1>
              <p className="text-sm text-amyu-muted mt-3 leading-relaxed">
                Présente le QR code à chaque visite pour ajouter un tampon. À 10
                tampons, on t&apos;offre le tiramisu de ton choix ♡
              </p>

              <div className="mt-6 space-y-3">
                <button
                  onClick={downloadCard}
                  data-testid="loyalty-download-btn"
                  className="w-full bg-coral text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-coral/90 transition-all"
                >
                  <Download size={16} /> Télécharger ma carte (PNG)
                </button>
                <p className="text-[11px] text-amyu-muted text-center px-3">
                  Une fois téléchargée, ouvre l&apos;image → « Partager » → « Ajouter
                  à Photos / Wallet » sur ton téléphone.
                </p>
              </div>

              <div className="mt-6 bg-white/70 backdrop-blur-md rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-amyu-muted">
                  Récap
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3 text-center">
                  <div>
                    <div className="font-display text-2xl text-coral">
                      {card.stamps}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mt-0.5">
                      Tampons
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl text-coral">
                      {card.total_earned}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mt-0.5">
                      Total gagnés
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl text-coral">
                      {card.total_redeemed}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mt-0.5">
                      Récompenses
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={logout}
                data-testid="loyalty-logout-btn"
                className="mt-6 inline-flex items-center gap-2 text-sm text-amyu-muted hover:text-coral"
              >
                <LogOut size={14} /> Ce n&apos;est pas ma carte
              </button>
              <ReviewMission
                card={card}
                onSuccess={() =>
                  axios.get(`${API}/loyalty/cards/${card.id}`).then((r) => setCard(r.data.card))
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
